import { Form, FormField, Input, ModalDialog, Select } from "@rothoblaas/design-system";
import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import type { MaterialCategory as MaterialCategoryCode } from "../../../../http/envelopeDesigner/types/material";
import {
  CLASSIFICATION_CODE,
  type MaterialCategory,
  type MaterialCategoryBody,
} from "../../../../http/envelopeDesigner/types/materialCategory";
import {
  useAdminEnvelopeDesignerApi,
  useMaterialClassificationApi,
} from "../../../../http/envelopeDesigner/useAdminEnvelopeDesigner";

type Option = { value: string; label: string };

// A category of the material library: its code (set on add only), its name in every language and the
// admissible-condensate category of its materials that have none.
const MaterialCategoryFormDialog: FC<{ category?: MaterialCategory; onClose: () => void }> = ({
  category,
  onClose,
}) => {
  const i18n = useI18n();
  const t = i18n.translation.envelopeDesignerModule.admin;
  const dispatch = useContext(NotificationDispatch);
  const classificationApi = useMaterialClassificationApi();
  const condCategories = useAdminEnvelopeDesignerApi().useAdminCondCategories().data?.items ?? [];
  const condCategoryOptions: Option[] = condCategories.map((c) => ({ value: c.code, label: `${c.name} (${c.rule})` }));

  const defaultValues: MaterialCategoryBody = {
    code: category?.code ?? ("" as MaterialCategoryCode),
    name: category?.name ?? "",
    nameIt: category?.nameIt ?? null,
    nameDe: category?.nameDe ?? null,
    nameFr: category?.nameFr ?? null,
    nameEs: category?.nameEs ?? null,
    condCategory: category?.condCategory ?? "",
  };

  const onSubmit = () => async (formData: MaterialCategoryBody) => {
    const body: MaterialCategoryBody = {
      ...formData,
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameFr: formData.nameFr || null,
      nameEs: formData.nameEs || null,
    };
    try {
      if (category) {
        await classificationApi.categories.update(category.code, body);
      } else {
        await classificationApi.categories.insert(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${category ? i18n.translation.common.edit : i18n.translation.common.add} ${t.category}`}
    >
      <Form<MaterialCategoryBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.code} error={form.formState.errors.code}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={!!category}
                  {...form.register("code", { required: true, pattern: CLASSIFICATION_CODE })}
                />
              )}
            </FormField>
            <TranslatableTextField form={form} />
            <Controller
              name="condCategory"
              control={form.control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={t.condCategory} error={fieldState.error}>
                  {({ labelId }) => (
                    <Select<Option, false>
                      inputId={labelId}
                      value={condCategoryOptions.find((option) => option.value === field.value) ?? null}
                      options={condCategoryOptions}
                      placeholder={`${i18n.translation.common.select}...`}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option?.value ?? "")}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default MaterialCategoryFormDialog;
